import { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import BrandFrame from "@/components/BrandFrame";
import {
  Crop, RotateCw, RotateCcw, FlipHorizontal, FlipVertical, SlidersHorizontal,
  Wand2, Type, Palette, X, Check, Save, RefreshCw, Loader2, Trash2, Image as ImageIcon, Frame,
  Download, Upload,
} from "lucide-react";

const ASPECTS = [
  { key: "free", label: "Freeform", ratio: null },
  { key: "1:1", label: "Square 1:1", ratio: 1 },
  { key: "4:5", label: "Portrait 4:5", ratio: 4 / 5 },
  { key: "16:9", label: "Landscape 16:9", ratio: 16 / 9 },
  { key: "9:16", label: "Story 9:16", ratio: 9 / 16 },
];

const PRESETS = {
  original: { brightness: 0, contrast: 0, saturation: 0, warmth: 0, grayscale: false },
  vivid: { brightness: 0, contrast: 15, saturation: 35, warmth: 0, grayscale: false },
  soft: { brightness: 8, contrast: -10, saturation: -8, warmth: 0, grayscale: false },
  warm: { brightness: 5, contrast: 0, saturation: 5, warmth: 40, grayscale: false },
  cool: { brightness: 0, contrast: 0, saturation: 5, warmth: -40, grayscale: false },
  bw: { brightness: 0, contrast: 10, saturation: 0, warmth: 0, grayscale: true },
};
const PRESET_LIST = [
  { key: "original", label: "Original" }, { key: "vivid", label: "Vivid" },
  { key: "soft", label: "Soft" }, { key: "warm", label: "Warm" },
  { key: "cool", label: "Cool" }, { key: "bw", label: "B&W" },
];

const DEFAULT_ADJ = { brightness: 0, contrast: 0, saturation: 0, warmth: 0, grayscale: false };
const TEAL = "#5FA8B4";

function filterString(a) {
  const b = 1 + a.brightness / 100;
  const c = 1 + a.contrast / 100;
  const s = 1 + a.saturation / 100;
  let f = `brightness(${b}) contrast(${c}) saturate(${s})`;
  if (a.warmth > 0) f += ` sepia(${(a.warmth / 100) * 0.55})`;
  else if (a.warmth < 0) f += ` hue-rotate(${(a.warmth / 100) * 22}deg)`;
  if (a.grayscale) f += ` grayscale(1)`;
  return f;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export default function ImageEditor({ open, onOpenChange, src, filename = "image.png", onSaved, swap, resubmitOf }) {
  const api = useApi();
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [img, setImg] = useState(null);
  const [tool, setTool] = useState("adjust");
  const [adj, setAdj] = useState(DEFAULT_ADJ);
  const [preset, setPreset] = useState("original");
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [appliedCrop, setAppliedCrop] = useState(null); // {x,y,w,h} natural
  const [cropRatio, setCropRatio] = useState("free");
  const [cropRect, setCropRect] = useState(null); // preview px {x,y,w,h} while in crop mode
  const [texts, setTexts] = useState([]);
  const [hasTransparency, setHasTransparency] = useState(false);
  const [bg, setBg] = useState({ type: "none", color: "#FFFFFF", color2: "#5FA8B4", angle: 90 });
  const [brandColors, setBrandColors] = useState([TEAL, "#3A7A8C", "#3C4048", "#7A9090"]);
  const [removingBg, setRemovingBg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [clientScale, setClientScale] = useState(1);
  const [savedMedia, setSavedMedia] = useState(null);
  const [frameSrc, setFrameSrc] = useState(null);

  // ---- output geometry (natural px) ----
  const geom = useCallback(() => {
    if (!img) return { outW: 0, outH: 0, cw: 0, ch: 0, cx: 0, cy: 0 };
    const crop = appliedCrop || { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight };
    const rad = (rotation * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad)), sin = Math.abs(Math.sin(rad));
    const outW = crop.w * cos + crop.h * sin;
    const outH = crop.w * sin + crop.h * cos;
    return { outW, outH, cw: crop.w, ch: crop.h, cx: crop.x, cy: crop.y };
  }, [img, appliedCrop, rotation]);

  // ---- core render ----
  const render = useCallback((canvas, scale, { cropMode } = {}) => {
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    let outW, outH, cw, ch, cx, cy;
    if (cropMode) {
      outW = img.naturalWidth; outH = img.naturalHeight;
      cw = outW; ch = outH; cx = 0; cy = 0;
    } else {
      const g = geom(); outW = g.outW; outH = g.outH; cw = g.cw; ch = g.ch; cx = g.cx; cy = g.cy;
    }
    canvas.width = Math.max(1, Math.round(outW * scale));
    canvas.height = Math.max(1, Math.round(outH * scale));
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!cropMode && bg.type !== "none") {
      if (bg.type === "solid") {
        ctx.fillStyle = bg.color;
      } else {
        const rad = (bg.angle * Math.PI) / 180;
        const x2 = Math.cos(rad) * canvas.width, y2 = Math.sin(rad) * canvas.height;
        const grad = ctx.createLinearGradient(0, 0, x2, y2);
        grad.addColorStop(0, bg.color); grad.addColorStop(1, bg.color2);
        ctx.fillStyle = grad;
      }
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    if (!cropMode) ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.filter = filterString(adj);
    ctx.drawImage(img, cx, cy, cw, ch, (-cw / 2) * scale, (-ch / 2) * scale, cw * scale, ch * scale);
    ctx.restore();
  }, [img, geom, bg, rotation, flipH, flipV, adj]);

  // ---- preview scale ----
  const previewScale = useCallback(() => {
    if (!img) return 1;
    const maxW = 620, maxH = 440;
    let ow, oh;
    if (tool === "crop") { ow = img.naturalWidth; oh = img.naturalHeight; }
    else { const g = geom(); ow = g.outW; oh = g.outH; }
    return Math.min(maxW / ow, maxH / oh, 1) || 1;
  }, [img, tool, geom]);

  // redraw preview whenever anything changes
  useEffect(() => {
    if (!open || !img) return;
    let raf = requestAnimationFrame(() => render(canvasRef.current, previewScale(), { cropMode: tool === "crop" }));
    return () => cancelAnimationFrame(raf);
  }, [open, img, render, previewScale, tool, adj, rotation, flipH, flipV, appliedCrop, bg, texts]);

  // measure client scale for text overlay sizing
  useLayoutEffect(() => {
    const measure = () => {
      const c = canvasRef.current;
      if (!c || !c.width) return;
      const ps = previewScale();
      const s = (c.clientWidth / c.width) * ps;
      setClientScale(Number.isFinite(s) && s > 0 ? s : 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [previewScale, tool, appliedCrop, rotation, img]);

  // load image + brand + usage on open
  useEffect(() => {
    if (!open || !src) return;
    setImg(null); setAdj(DEFAULT_ADJ); setPreset("original"); setRotation(0);
    setFlipH(false); setFlipV(false); setAppliedCrop(null); setCropRatio("free");
    setCropRect(null); setTexts([]); setHasTransparency(false);
    setBg({ type: "none", color: "#FFFFFF", color2: TEAL, angle: 90 });
    setTool("adjust"); setSavedMedia(null);
    loadImage(src).then(setImg).catch(() => toast.error("Couldn't load image for editing"));
    api.get("/brand").then((r) => {
      const pal = (r.data?.palette || []).map((p) => p.hex).filter(Boolean);
      if (pal.length) setBrandColors(pal);
    }).catch(() => {});
    api.get("/media/remove-bg/usage").then((r) => setRemaining(r.data.remaining)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, src]);

  const applyPreset = (key) => {
    setPreset(key);
    setAdj({ ...PRESETS[key] });
  };
  const setAdjVal = (k, v) => { setAdj((a) => ({ ...a, [k]: v })); setPreset("custom"); };

  // ---- crop interactions ----
  const startCrop = () => {
    setTool("crop");
    const s = previewScaleFor();
    initCropBox("free", s);
  };
  const previewScaleFor = () => {
    if (!img) return 1;
    return Math.min(620 / img.naturalWidth, 440 / img.naturalHeight, 1) || 1;
  };
  const initCropBox = (ratioKey, scale) => {
    if (!img) return;
    const s = scale || previewScaleFor();
    const W = img.naturalWidth * s, H = img.naturalHeight * s;
    const r = ASPECTS.find((a) => a.key === ratioKey)?.ratio;
    let w, h;
    if (!r) { w = W * 0.8; h = H * 0.8; }
    else { if (W / H > r) { h = H * 0.9; w = h * r; } else { w = W * 0.9; h = w / r; } }
    setCropRect({ x: (W - w) / 2, y: (H - h) / 2, w, h });
  };
  const chooseRatio = (key) => { setCropRatio(key); initCropBox(key); };

  const dragState = useRef(null);
  const onCropPointerDown = (e, mode) => {
    e.preventDefault(); e.stopPropagation();
    dragState.current = { mode, sx: e.clientX, sy: e.clientY, rect: { ...cropRect } };
    window.addEventListener("pointermove", onCropPointerMove);
    window.addEventListener("pointerup", onCropPointerUp);
  };
  const onCropPointerMove = (e) => {
    const d = dragState.current; if (!d) return;
    const c = canvasRef.current; const W = c.width, H = c.height;
    const r = ASPECTS.find((a) => a.key === cropRatio)?.ratio;
    let { x, y, w, h } = d.rect;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (d.mode === "move") {
      x = Math.max(0, Math.min(W - w, x + dx));
      y = Math.max(0, Math.min(H - h, y + dy));
    } else {
      // corner resize: se handle
      let nw = Math.max(40, w + dx), nh = Math.max(40, h + dy);
      if (r) { nh = nw / r; }
      nw = Math.min(nw, W - x); nh = Math.min(nh, H - y);
      if (r) { if (nw / nh > r) nw = nh * r; else nh = nw / r; }
      w = nw; h = nh;
    }
    setCropRect({ x, y, w, h });
  };
  const onCropPointerUp = () => {
    dragState.current = null;
    window.removeEventListener("pointermove", onCropPointerMove);
    window.removeEventListener("pointerup", onCropPointerUp);
  };
  const confirmCrop = () => {
    if (!cropRect || !img) { setTool("adjust"); return; }
    const s = previewScaleFor();
    setAppliedCrop({
      x: Math.round(cropRect.x / s), y: Math.round(cropRect.y / s),
      w: Math.round(cropRect.w / s), h: Math.round(cropRect.h / s),
    });
    setCropRect(null); setTool("adjust");
  };
  const cancelCrop = () => { setCropRect(null); setTool("adjust"); };

  // ---- text ----
  const addText = () => {
    if (texts.length >= 2) { toast.error("Max 2 text overlays"); return; }
    setTexts((t) => [...t, { id: Date.now(), text: "Your Brand", x: 0.5, y: 0.5, size: 48, color: TEAL }]);
  };
  const updateText = (id, patch) => setTexts((t) => t.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removeText = (id) => setTexts((t) => t.filter((x) => x.id !== id));

  const textDrag = useRef(null);
  const onTextPointerDown = (e, id) => {
    e.preventDefault(); e.stopPropagation();
    textDrag.current = { id };
    window.addEventListener("pointermove", onTextPointerMove);
    window.addEventListener("pointerup", onTextPointerUp);
  };
  const onTextPointerMove = (e) => {
    const d = textDrag.current; if (!d) return;
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    updateText(d.id, { x, y });
  };
  const onTextPointerUp = () => {
    textDrag.current = null;
    window.removeEventListener("pointermove", onTextPointerMove);
    window.removeEventListener("pointerup", onTextPointerUp);
  };

  // ---- background removal ----
  const doRemoveBg = async () => {
    if (!img) return;
    setRemovingBg(true);
    try {
      const c = document.createElement("canvas");
      render(c, 1); // current natural-res composite (without text)
      const dataUrl = c.toDataURL("image/png");
      const res = await api.post("/media/remove-bg", { data_url: dataUrl });
      const newImg = await loadImage(res.data.data_url);
      setImg(newImg);
      setAppliedCrop(null); setRotation(0); setFlipH(false); setFlipV(false);
      setHasTransparency(true);
      setRemaining(res.data.remaining);
      setTool("background");
      toast.success("Background removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove background. Please try again or use a different image.");
    } finally { setRemovingBg(false); }
  };

  // ---- export ----
  const buildExport = () => {
    const c = document.createElement("canvas");
    render(c, 1);
    // draw text overlays in output space
    const ctx = c.getContext("2d");
    texts.forEach((t) => {
      ctx.font = `700 ${t.size}px Montserrat, sans-serif`;
      ctx.fillStyle = t.color;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(t.text, t.x * c.width, t.y * c.height);
    });
    return c;
  };

  const reset = () => {
    setAdj(DEFAULT_ADJ); setPreset("original"); setRotation(0); setFlipH(false);
    setFlipV(false); setAppliedCrop(null); setTexts([]);
    setBg({ type: "none", color: "#FFFFFF", color2: TEAL, angle: 90 });
    loadImage(src).then(setImg).catch(() => {});
    setHasTransparency(false);
  };

  // ---- download to device ----
  const download = () => {
    try {
      const c = buildExport();
      const isPng = hasTransparency || bg.type === "none";
      const mime = isPng ? "image/png" : "image/jpeg";
      const dataUrl = c.toDataURL(mime, 0.95);
      const base = (filename || "image").replace(/\.[^.]+$/, "");
      const ts = Date.now();
      const outName = `${base}-edited-${ts}.${isPng ? "png" : "jpg"}`;
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = outName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Downloaded to your device");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  // ---- replace with an uploaded image ----
  const handleReplaceFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) {
      toast.error("Please upload a PNG or JPG image");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      loadImage(reader.result)
        .then((newImg) => {
          setImg(newImg);
          setAdj(DEFAULT_ADJ); setPreset("original"); setRotation(0);
          setFlipH(false); setFlipV(false); setAppliedCrop(null); setCropRatio("free");
          setCropRect(null); setTexts([]); setHasTransparency(false);
          setBg({ type: "none", color: "#FFFFFF", color2: TEAL, angle: 90 });
          setTool("adjust"); setSavedMedia(null);
          toast.success("Image replaced — keep editing below");
        })
        .catch(() => toast.error("Couldn't load that image"));
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const c = buildExport();
      const isPng = hasTransparency || bg.type === "none";
      const mime = isPng ? "image/png" : "image/jpeg";
      const dataUrl = c.toDataURL(mime, 0.95);
      const base = (filename || "image").replace(/\.[^.]+$/, "");
      const ts = Date.now();
      const outName = `${base}-edited-${ts}.${isPng ? "png" : "jpg"}`;
      const res = await api.post("/media/upload-data-url", { data_url: dataUrl, filename: outName, tags: ["edited"], resubmit_of: resubmitOf });
      setSavedMedia(res.data);
      toast.success("Saved to Media Library");
      onSaved && onSaved(res.data);
      if (!swap) onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const TOOLS = [
    { key: "adjust", label: "Adjust", icon: SlidersHorizontal },
    { key: "filters", label: "Filters", icon: ImageIcon },
    { key: "crop", label: "Crop", icon: Crop },
    { key: "transform", label: "Rotate", icon: RotateCw },
    { key: "text", label: "Text", icon: Type },
    { key: "removebg", label: "Cut out", icon: Wand2 },
    { key: "background", label: "Background", icon: Palette },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#e5e7eb] text-[#3C4048] max-w-5xl w-[96vw] p-0 overflow-hidden" data-testid="image-editor">
        <DialogTitle className="sr-only">Image Editor</DialogTitle>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#e5e7eb] flex-wrap gap-y-2">
          <h2 className="font-bold text-lg" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Image Editor</h2>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <input ref={replaceInputRef} type="file" accept="image/png,image/jpeg" className="hidden"
              onChange={handleReplaceFile} data-testid="replace-image-input" />
            <Button data-testid="editor-reset" onClick={reset} variant="outline" size="sm" className="border-[#e5e7eb] text-[#3C4048] rounded-[10px] gap-1">
              <RefreshCw size={14} /> Reset
            </Button>
            <Button data-testid="editor-replace" onClick={() => replaceInputRef.current?.click()} variant="outline" size="sm" className="border-[#e5e7eb] text-[#3C4048] rounded-[10px] gap-1">
              <Upload size={14} /> Replace image
            </Button>
            <Button data-testid="editor-brand-frame" onClick={() => { const c = buildExport(); setFrameSrc(c.toDataURL("image/png")); }} variant="outline" size="sm" className="border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] gap-1">
              <Frame size={14} /> Brand Frame
            </Button>
            <Button data-testid="editor-download" onClick={download} disabled={!img} variant="outline" size="sm" className="border-[#e5e7eb] text-[#3C4048] rounded-[10px] gap-1">
              <Download size={14} /> Download
            </Button>
            <Button data-testid="editor-save" onClick={save} disabled={saving || !img} size="sm" className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[10px] gap-1">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </Button>
            <button data-testid="editor-close" onClick={() => onOpenChange(false)} className="text-[#7A9090] hover:text-[#3C4048] p-1"><X size={20} /></button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row">
          {/* Canvas */}
          <div ref={wrapRef} className="flex-1 bg-[#f5f5f5] flex items-center justify-center p-4 min-h-[300px] relative"
            style={{ backgroundImage: "linear-gradient(45deg,#eef1f2 25%,transparent 25%),linear-gradient(-45deg,#eef1f2 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eef1f2 75%),linear-gradient(-45deg,transparent 75%,#eef1f2 75%)", backgroundSize: "20px 20px", backgroundPosition: "0 0,0 10px,10px -10px,-10px 0" }}>
            <div className="relative" style={{ lineHeight: 0 }}>
              <canvas ref={canvasRef} data-testid="editor-canvas" className="max-w-full h-auto rounded-[8px] shadow-sm bg-white" style={{ maxHeight: "56vh" }} />

              {/* crop overlay */}
              {tool === "crop" && cropRect && (
                <div className="absolute inset-0" data-testid="crop-overlay">
                  <div
                    className="absolute border-2 border-[#5FA8B4] cursor-move"
                    style={{ left: cropRect.x + "px", top: cropRect.y + "px", width: cropRect.w + "px", height: cropRect.h + "px", boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)" }}
                    onPointerDown={(e) => onCropPointerDown(e, "move")}
                    data-testid="crop-box"
                  >
                    <div className="absolute -right-2 -bottom-2 w-4 h-4 bg-[#5FA8B4] rounded-full cursor-se-resize"
                      onPointerDown={(e) => onCropPointerDown(e, "resize")} data-testid="crop-handle" />
                  </div>
                </div>
              )}

              {/* text overlays */}
              {tool !== "crop" && texts.map((t) => (
                <div key={t.id}
                  data-testid={`text-overlay-${t.id}`}
                  onPointerDown={(e) => onTextPointerDown(e, t.id)}
                  className="absolute cursor-move select-none whitespace-nowrap"
                  style={{
                    left: t.x * 100 + "%", top: t.y * 100 + "%", transform: "translate(-50%,-50%)",
                    fontFamily: "Montserrat, sans-serif", fontWeight: 700,
                    fontSize: Math.max(8, t.size * clientScale) + "px", color: t.color,
                  }}>
                  {t.text}
                </div>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="w-full md:w-[300px] border-t md:border-t-0 md:border-l border-[#e5e7eb] flex flex-col">
            {/* tool tabs */}
            <div className="flex md:grid md:grid-cols-4 gap-1 p-2 border-b border-[#e5e7eb] overflow-x-auto">
              {TOOLS.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.key} data-testid={`tool-${t.key}`}
                    onClick={() => (t.key === "crop" ? startCrop() : setTool(t.key))}
                    className={`flex flex-col items-center gap-1 py-2 px-1 rounded-[8px] text-[10px] font-medium shrink-0 min-w-[52px] transition-colors ${tool === t.key ? "bg-[#eef7f8] text-[#3A7A8C]" : "text-[#7A9090] hover:bg-[#f5f5f5]"}`}>
                    <Icon size={16} /> {t.label}
                  </button>
                );
              })}
            </div>

            <div className="p-4 space-y-4 overflow-y-auto" style={{ maxHeight: "48vh" }}>
              {tool === "adjust" && (
                <div className="space-y-4" data-testid="panel-adjust">
                  {[["brightness", "Brightness"], ["contrast", "Contrast"], ["saturation", "Saturation"], ["warmth", "Warmth"]].map(([k, label]) => (
                    <div key={k}>
                      <div className="flex justify-between text-xs text-[#7A9090] mb-1"><span>{label}</span><span>{adj[k]}</span></div>
                      <input type="range" min={-100} max={100} value={adj[k]} data-testid={`adj-${k}`}
                        onChange={(e) => setAdjVal(k, Number(e.target.value))}
                        className="w-full accent-[#5FA8B4]" />
                    </div>
                  ))}
                </div>
              )}

              {tool === "filters" && (
                <div className="grid grid-cols-3 gap-2" data-testid="panel-filters">
                  {PRESET_LIST.map((p) => (
                    <button key={p.key} data-testid={`filter-${p.key}`} onClick={() => applyPreset(p.key)}
                      className={`py-3 rounded-[8px] text-xs font-medium border ${preset === p.key ? "border-[#5FA8B4] bg-[#eef7f8] text-[#3A7A8C]" : "border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5]"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              )}

              {tool === "crop" && (
                <div className="space-y-3" data-testid="panel-crop">
                  <p className="text-xs text-[#7A9090]">Choose a ratio, drag to reposition, drag the corner to resize.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {ASPECTS.map((a) => (
                      <button key={a.key} data-testid={`crop-ratio-${a.key}`} onClick={() => chooseRatio(a.key)}
                        className={`py-2 rounded-[8px] text-xs font-medium border ${cropRatio === a.key ? "border-[#5FA8B4] bg-[#eef7f8] text-[#3A7A8C]" : "border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5]"}`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button data-testid="crop-apply" onClick={confirmCrop} size="sm" className="flex-1 bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[10px] gap-1"><Check size={14} /> Apply</Button>
                    <Button data-testid="crop-cancel" onClick={cancelCrop} size="sm" variant="outline" className="flex-1 border-[#e5e7eb] text-[#3C4048] rounded-[10px]">Cancel</Button>
                  </div>
                </div>
              )}

              {tool === "transform" && (
                <div className="space-y-4" data-testid="panel-transform">
                  <div className="grid grid-cols-2 gap-2">
                    <Button data-testid="rotate-left" onClick={() => setRotation((r) => r - 90)} variant="outline" size="sm" className="border-[#e5e7eb] text-[#3C4048] rounded-[10px] gap-1"><RotateCcw size={14} /> 90° left</Button>
                    <Button data-testid="rotate-right" onClick={() => setRotation((r) => r + 90)} variant="outline" size="sm" className="border-[#e5e7eb] text-[#3C4048] rounded-[10px] gap-1"><RotateCw size={14} /> 90° right</Button>
                    <Button data-testid="flip-h" onClick={() => setFlipH((v) => !v)} variant="outline" size="sm" className={`rounded-[10px] gap-1 ${flipH ? "border-[#5FA8B4] bg-[#eef7f8] text-[#3A7A8C]" : "border-[#e5e7eb] text-[#3C4048]"}`}><FlipHorizontal size={14} /> Flip H</Button>
                    <Button data-testid="flip-v" onClick={() => setFlipV((v) => !v)} variant="outline" size="sm" className={`rounded-[10px] gap-1 ${flipV ? "border-[#5FA8B4] bg-[#eef7f8] text-[#3A7A8C]" : "border-[#e5e7eb] text-[#3C4048]"}`}><FlipVertical size={14} /> Flip V</Button>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-[#7A9090] mb-1"><span>Fine rotation</span><span>{rotation % 360}°</span></div>
                    <input type="range" min={-45} max={45} value={((rotation % 90) + 90) % 90 > 45 ? (((rotation % 90) + 90) % 90) - 90 : ((rotation % 90) + 90) % 90}
                      data-testid="rotate-fine"
                      onChange={(e) => setRotation((r) => Math.round(r / 90) * 90 + Number(e.target.value))}
                      className="w-full accent-[#5FA8B4]" />
                  </div>
                </div>
              )}

              {tool === "text" && (
                <div className="space-y-3" data-testid="panel-text">
                  <Button data-testid="add-text" onClick={addText} disabled={texts.length >= 2} size="sm" className="w-full bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[10px] gap-1"><Type size={14} /> Add text ({texts.length}/2)</Button>
                  {texts.map((t) => (
                    <div key={t.id} className="border border-[#e5e7eb] rounded-[10px] p-3 space-y-2" data-testid={`text-editor-${t.id}`}>
                      <div className="flex gap-2">
                        <Input value={t.text} data-testid={`text-input-${t.id}`} onChange={(e) => updateText(t.id, { text: e.target.value })} className="border-[#e5e7eb] text-[#3C4048] h-8 text-sm" />
                        <button onClick={() => removeText(t.id)} className="text-[#ef4444] p-1"><Trash2 size={16} /></button>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs text-[#7A9090] mb-1"><span>Size</span><span>{t.size}</span></div>
                        <input type="range" min={12} max={200} value={t.size} data-testid={`text-size-${t.id}`} onChange={(e) => updateText(t.id, { size: Number(e.target.value) })} className="w-full accent-[#5FA8B4]" />
                      </div>
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {brandColors.map((c) => (
                          <button key={c} data-testid={`text-color-${t.id}-${c}`} onClick={() => updateText(t.id, { color: c })} className="w-6 h-6 rounded-full border border-[#e5e7eb]" style={{ backgroundColor: c }} />
                        ))}
                        <input type="color" value={t.color} onChange={(e) => updateText(t.id, { color: e.target.value })} className="w-6 h-6 rounded border-0 bg-transparent p-0" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tool === "removebg" && (
                <div className="space-y-3" data-testid="panel-removebg">
                  <p className="text-xs text-[#7A9090]">Automatically erase the background. The result is a transparent PNG you can keep editing.</p>
                  {remaining !== null && <p className="text-xs text-[#3A7A8C] font-medium" data-testid="removebg-remaining">{remaining} removals left this month</p>}
                  <Button data-testid="remove-bg-btn" onClick={doRemoveBg} disabled={removingBg} className="w-full bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[10px] gap-1">
                    {removingBg ? <><Loader2 size={14} className="animate-spin" /> Removing…</> : <><Wand2 size={14} /> Remove Background</>}
                  </Button>
                </div>
              )}

              {tool === "background" && (
                <div className="space-y-3" data-testid="panel-background">
                  {!hasTransparency && <p className="text-xs text-[#7A9090]">Backgrounds show behind transparent areas. Use "Cut out" first to remove the existing background.</p>}
                  <div className="grid grid-cols-3 gap-2">
                    {[["none", "Transparent"], ["solid", "Solid"], ["gradient", "Gradient"]].map(([k, label]) => (
                      <button key={k} data-testid={`bg-type-${k}`} onClick={() => setBg((b) => ({ ...b, type: k }))}
                        className={`py-2 rounded-[8px] text-xs font-medium border ${bg.type === k ? "border-[#5FA8B4] bg-[#eef7f8] text-[#3A7A8C]" : "border-[#e5e7eb] text-[#3C4048]"}`}>{label}</button>
                    ))}
                  </div>
                  {bg.type !== "none" && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {brandColors.concat(["#FFFFFF", "#F5F5F5"]).map((c) => (
                          <button key={c} data-testid={`bg-color-${c}`} onClick={() => setBg((b) => ({ ...b, color: c }))} className="w-6 h-6 rounded-full border border-[#e5e7eb]" style={{ backgroundColor: c }} />
                        ))}
                        <input type="color" value={bg.color} onChange={(e) => setBg((b) => ({ ...b, color: e.target.value }))} className="w-6 h-6 rounded border-0 bg-transparent p-0" />
                      </div>
                      {bg.type === "gradient" && (
                        <>
                          <div className="flex items-center gap-2 text-xs text-[#7A9090]">2nd colour <input type="color" value={bg.color2} onChange={(e) => setBg((b) => ({ ...b, color2: e.target.value }))} className="w-6 h-6 rounded border-0 bg-transparent p-0" /></div>
                          <div>
                            <div className="flex justify-between text-xs text-[#7A9090] mb-1"><span>Angle</span><span>{bg.angle}°</span></div>
                            <input type="range" min={0} max={360} value={bg.angle} onChange={(e) => setBg((b) => ({ ...b, angle: Number(e.target.value) }))} className="w-full accent-[#5FA8B4]" />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {swap && savedMedia && (
              <div className="p-3 border-t border-[#e5e7eb]">
                <Button data-testid="editor-swap" onClick={() => { swap.handler(savedMedia); onOpenChange(false); }} className="w-full bg-[#3A7A8C] hover:bg-[#2c5f6e] text-white rounded-[10px] gap-1">
                  <Check size={14} /> {swap.label || "Use in this post"}
                </Button>
              </div>
            )}
          </div>
        </div>

        {frameSrc && (
          <BrandFrame
            open={!!frameSrc}
            onOpenChange={(v) => !v && setFrameSrc(null)}
            src={frameSrc}
            filename={filename}
            onSaved={(m) => { setFrameSrc(null); onSaved && onSaved(m); if (!swap) onOpenChange(false); }}
            swap={swap}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
